import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useV1CustomPage } from '../hooks/useV1CustomPages';
import { useEventContext } from '../../contexts/EventContext';
import { useAccount } from '../../../hooks/useAccount';
import { useEventDetails } from '../../../hooks/useEventDetails';
import { V1SectionRenderer } from './sections/V1SectionRenderer';
import { Lock, UserCheck } from 'lucide-react';

/**
 * V1 Custom Page - FROZEN
 *
 * Displays custom content pages for V1 events.
 * Handles page and section level access control.
 *
 * DO NOT add V2 features to this component.
 */
export const V1CustomPage: React.FC = () => {
  const { pageSlug } = useParams<{ pageSlug: string }>();
  const { eventSlug } = useEventContext();
  const { data: page, isLoading, error } = useV1CustomPage(eventSlug, pageSlug!);
  const { data: account } = useAccount();
  const { data: eventDetails } = useEventDetails(eventSlug);

  // Update page title
  useEffect(() => {
    if (page && eventDetails) {
      document.title = `${page.title} - ${eventDetails.name}`;
    }
  }, [page, eventDetails]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-red-800 text-lg font-semibold mb-2">Error Loading Page</h2>
          <p className="text-red-600">
            {error instanceof Error ? error.message : 'An error occurred while loading the page.'}
          </p>
        </div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-yellow-800 text-lg font-semibold mb-2">Page Not Found</h2>
          <p className="text-yellow-600">
            The requested page could not be found.
          </p>
        </div>
      </div>
    );
  }

  // Check page-level access
  const isRegistered = eventDetails?.currentUserRegistered || false;

  if (page.requireAccount && !account) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Lock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-gray-800 text-xl font-semibold mb-2">
            Sign In Required
          </h2>
          <p className="text-gray-600 mb-4">
            You must be signed in to view this page.
          </p>
          <a
            href={`/${eventSlug}`}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Event
          </a>
        </div>
      </div>
    );
  }

  if (page.requireRegistration && !isRegistered) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <UserCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h2 className="text-gray-800 text-xl font-semibold mb-2">
            Registration Required
          </h2>
          <p className="text-gray-600 mb-4">
            You must be registered for this event to view this page.
          </p>
          {eventDetails?.registrationOpen ? (
            <a
              href={`/${eventSlug}/registrations/new`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Register Now
            </a>
          ) : (
            <a
              href={`/${eventSlug}`}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700"
            >
              Back to Event
            </a>
          )}
        </div>
      </div>
    );
  }

  // Check if we should show registered details
  const showRegisteredDetails = !!(page.enableRegisteredDetails && isRegistered && page.registeredDetails);

  // Render sections or registered details
  const sections = page.sections || [];

  console.log('sections:', sections);
  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <>
        {/* Content sections */}
        <div className="space-y-8">
          {sections.map((section: any) => (
            <div key={section.id} className="mb-6">
              <V1SectionRenderer section={section} showRegisteredDetails={showRegisteredDetails} registeredDetails={page.registeredDetails} />
            </div>
          ))}
        </div>

        {sections.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">This page has no content sections.</p>
          </div>
        )}
      </>
    </div>
  );
};